// The wire of the configuration service as the console speaks it: the five
// operations, their subjects in the tenant form, the expect-revision header,
// the envelope every reply carries inside its sealed body, the watch's
// headers and the error codes (02-DESIGN/06-configuration/01-the-configuration-service.md;
// decisions 0012 and 0031). The family is account-local: a browser writes the
// tokenless form and reaches the configuration service placed in its own
// tenant; there is no platform form. The service side — the subscription
// filter, the bucket key, the parse — is the Go library's and the
// configuration service's.
import { headers as newHeaders } from "@nats-io/nats-core";
import { utf8Decode, utf8Encode } from "./encoding.js";
import { CODE_CANNOT_OPEN, ServiceError } from "./envelope.js";
import { audienceOf, CARRIER_HEADER } from "./identity.js";
import { client, Family, join, validToken } from "./subject.js";
import * as watch from "./watch.js";
/** SERVICE is the configuration service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "config";
/** The five operations. */
export const Op = {
    set: "set",
    get: "get",
    delete: "delete",
    list: "list",
    watch: "watch",
};
/** OPS lists the five operations. */
export const OPS = Object.values(Op);
/** validOp reports whether s is one of the five. */
export function validOp(s) {
    return OPS.includes(s);
}
/**
 * Kind names what is configured and decides how the rest of the subject is
 * read: `workload`, whose asset is a workload id, and `solution`, whose asset
 * is a solution's name and whose documents are `settings` and `required`
 * (02-DESIGN/14-solutions/01-the-manifest.md § settings and secrets).
 */
export const Kind = {
    workload: "workload",
    solution: "solution",
};
/** validKind reports whether s is a kind the grammar knows. */
export function validKind(s) {
    return s === Kind.workload || s === Kind.solution;
}
/** served reports whether k is a kind the service serves today. */
export function served(k) {
    return validKind(k);
}
/** DOCUMENT_REQUIRED is the document beside settings an image or a solution resolves required over its settings (02-DESIGN/13-blueprints/03-required.md). */
export const DOCUMENT_REQUIRED = "required";
/** DOCUMENT_SETTINGS is the document every platform image and every workload on the bootstrap reads: the convention, not a vocabulary the service polices. */
export const DOCUMENT_SETTINGS = "settings";
/** NotAnObjectError is a document the resolution cannot read fields of. */
export class NotAnObjectError extends Error {
    constructor(which) {
        super(`config: the ${which} document is not a JSON object`);
        this.name = "NotAnObjectError";
    }
}
function objectOf(text, which) {
    let v;
    try {
        v = JSON.parse(text);
    }
    catch {
        throw new NotAnObjectError(which);
    }
    if (typeof v !== "object" || v === null || Array.isArray(v)) {
        throw new NotAnObjectError(which);
    }
    return v;
}
/**
 * resolve resolves a settings document and a required document into one,
 * required over settings, as impire-sdk-go/config.Resolve does: every
 * top-level field the required document carries replaces the same field of
 * settings, whole; an absent document is the empty string. Keys come out
 * sorted, as Go marshals a map.
 */
export function resolve(settings, required) {
    if (required === "") {
        return { value: settings, required: [] };
    }
    const req = objectOf(required, "required");
    const fields = Object.keys(req).sort();
    if (settings === "") {
        return { value: required, required: fields };
    }
    const merged = { ...objectOf(settings, "settings"), ...req };
    const sorted = {};
    for (const k of Object.keys(merged).sort()) {
        sorted[k] = merged[k];
    }
    return { value: JSON.stringify(sorted), required: fields };
}
/** MAX_DOCUMENT bounds a document, in bytes; the client refuses an over-limit document before it is published. */
export const MAX_DOCUMENT = 64 * 1024;
/** EXPECT_REVISION_HEADER on a set makes the write the bucket's own compare-and-set, refused conflict when anything landed in between. */
export const EXPECT_REVISION_HEADER = "Impire-Config-Expect-Revision";
/** SNAPSHOT_HEADER is `true` on a snapshot frame and `done` on the progress frame that completes the snapshot. */
export const SNAPSHOT_HEADER = "Impire-Config-Snapshot";
/** SINCE_HEADER on a reopened watch names the revision the reader holds. */
export const SINCE_HEADER = "Impire-Config-Since";
/** REVISION_HEADER on the done terminator is the last revision delivered. */
export const REVISION_HEADER = "Impire-Config-Revision";
/** DOCUMENT_HEADER narrows a watch of a prefix to the documents of one name. */
export const DOCUMENT_HEADER = "Impire-Config-Document";
/** WATCH_HEADERS is the leased watch instantiated with this family's headers. */
export const WATCH_HEADERS = {
    snapshot: SNAPSHOT_HEADER,
    since: SINCE_HEADER,
    revision: REVISION_HEADER,
};
/** Error codes, kebab-case as every family's are. */
export const Code = {
    /** A malformed subject, or a value that is not UTF-8 or over the limit. */
    badRequest: "bad-request",
    /** A set naming a revision that is no longer the document's. */
    conflict: "conflict",
    /**
     * A get of a document that does not exist, under a workload the caller may
     * reach: the one operation that reports absence, reached only once the
     * caller passed reachability, so a reply never says which workloads exist
     * (hq issue 028).
     */
    notFound: "not-found",
    /** Anything else: the bucket unavailable, a workload that does not exist, a member asking about a workload they did not register. */
    failed: "failed",
    cannotOpen: CODE_CANNOT_OPEN,
};
/** BadSubjectError is thrown for a subject whose tokens do not fit the grammar. */
export class BadSubjectError extends Error {
    constructor(message = "config: not a configuration subject") {
        super(message);
        this.name = "BadSubjectError";
    }
}
/** pathTokens validates and composes the tokens after the operation. */
function pathTokens(op, kind, asset, document) {
    if (!validOp(op)) {
        throw new Error(`config: unknown operation "${op}"`);
    }
    if (!validKind(kind) || !validToken(asset)) {
        throw new BadSubjectError();
    }
    switch (op) {
        case Op.set:
        case Op.get:
        case Op.delete:
            if (!validToken(document)) {
                throw new BadSubjectError();
            }
            break;
        case Op.list:
            if (document !== "") {
                throw new BadSubjectError();
            }
            break;
        case Op.watch:
            if (document !== "" && !validToken(document)) {
                throw new BadSubjectError();
            }
            break;
    }
    const toks = [op, kind, asset];
    if (document !== "") {
        toks.push(document);
    }
    return toks;
}
/**
 * subject builds the tenant form a caller in a tenant writes:
 * `config.<op>.<kind>.<asset>[.<document>]`. list takes no document; watch
 * takes one or none; set, get and delete take one.
 */
export function subject(op, kind, asset, document = "") {
    return client(Family.config, ...pathTokens(op, kind, asset, document));
}
/**
 * path is the member path the envelope's key carries,
 * `<kind>.<asset>.<document>`: what the caller wrote, never the bucket key.
 * Without a document it is the asset's prefix.
 */
export function path(kind, asset, document = "") {
    return document === "" ? join(kind, asset) : join(kind, asset, document);
}
// A lone surrogate is the one way a JavaScript string fails to be UTF-8 text.
const loneSurrogate = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;
/** validDocument reports whether value is a document the service accepts: UTF-8 text of at most MAX_DOCUMENT bytes. */
export function validDocument(value) {
    if (loneSurrogate.test(value)) {
        return false;
    }
    return utf8Encode(value).length <= MAX_DOCUMENT;
}
/** EntryOp is what an entry records: a document written, or removed. */
export const EntryOp = {
    put: "put",
    delete: "delete",
};
/** decodeEntry reads a watch frame's entry. */
export function decodeEntry(data) {
    return JSON.parse(utf8Decode(data));
}
/**
 * Client speaks the five operations for a caller in a tenant — an admin
 * writing a workload's documents, a workload reading its own — every request
 * and reply sealed. The tenant is the account the caller is in. Every call
 * takes the headers it must bear
 * beside the sealing header: the carrier.
 */
export class Client {
    nc;
    caller;
    carrier;
    timeout;
    idle;
    /** The kind every asset this client names is of. */
    kind;
    /** A client over a connection with the caller's ephemeral key. */
    constructor(nc, caller, opts = {}) {
        this.nc = nc;
        this.caller = caller;
        this.carrier = opts.carrier;
        this.timeout = opts.timeout ?? 5000;
        this.idle = opts.idle;
        this.kind = opts.kind ?? Kind.workload;
    }
    async call(op, workload, document, body, opts) {
        const subj = subject(op, this.kind, workload, document);
        const data = body === undefined ? new Uint8Array(0) : utf8Encode(body);
        let hdrs = opts.headers;
        if (this.carrier) {
            hdrs = newHeaders();
            if (opts.headers) {
                for (const [k, values] of opts.headers) {
                    for (const v of values) {
                        hdrs.append(k, v);
                    }
                }
            }
            hdrs.set(CARRIER_HEADER, await this.carrier(audienceOf(subj)));
        }
        const reply = await this.caller.request(this.nc, SERVICE, subj, data, {
            timeout: opts.timeout ?? this.timeout,
            headers: hdrs,
        });
        try {
            return JSON.parse(utf8Decode(reply.data));
        }
        catch (err) {
            throw new Error(`config: ${op} ${path(this.kind, workload, document)}: reply is not JSON`, {
                cause: err,
            });
        }
    }
    /**
     * set replaces a workload's document whole. expected, when given, is the
     * revision the document was read at, and the write is refused conflict when
     * anything landed since. The reply is the entry written, with its revision.
     * A document that is not UTF-8 text of at most MAX_DOCUMENT bytes is refused
     * bad-request before anything is published.
     */
    set(workload, document, value, expected, opts = {}) {
        if (!validDocument(value)) {
            return Promise.reject(new ServiceError(Code.badRequest, `a document is UTF-8 text of at most ${MAX_DOCUMENT} bytes`));
        }
        let hdrs = opts.headers;
        if (expected !== undefined) {
            hdrs = newHeaders();
            if (opts.headers) {
                for (const [k, values] of opts.headers) {
                    for (const v of values) {
                        hdrs.append(k, v);
                    }
                }
            }
            hdrs.set(EXPECT_REVISION_HEADER, String(expected));
        }
        return this.call(Op.set, workload, document, value, { ...opts, headers: hdrs });
    }
    /** get reads a document at its latest revision; the absent is answered not-found (hq issue 028). */
    get(workload, document, opts = {}) {
        return this.call(Op.get, workload, document, undefined, opts);
    }
    /** delete removes a document; deleting the absent succeeds. */
    delete(workload, document, opts = {}) {
        return this.call(Op.delete, workload, document, undefined, opts);
    }
    /** list is the workload's documents with their revisions, never their contents. */
    async list(workload, opts = {}) {
        const reply = await this.call(Op.list, workload, "", undefined, opts);
        return reply.entries;
    }
    /**
     * watch follows a workload's documents — one, or every one when document is
     * empty — as the leased watch: the snapshot, its completion, one event per
     * change, the lease reopened from the last revision so the caller never
     * sees the seam. The signal ends it; an error ends it too and is the
     * caller's. The headers given ride every request of the watch, and the
     * client's carrier is read at every lease.
     */
    watch(workload, document = "", opts = {}) {
        const subj = subject(Op.watch, this.kind, workload, document);
        return watch.open(this.nc, this.caller, {
            service: SERVICE,
            subject: subj,
            headers: opts.headers,
            watchHeaders: WATCH_HEADERS,
            carrier: this.carrier,
            idle: this.idle,
            signal: opts.signal,
        }, decodeEntry);
    }
}
//# sourceMappingURL=config.js.map